"use client";

import * as React from "react";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import type { HireDepartmentDetail } from "~/lib/marketplace/hire-recommendations-pricing";
import {
  rosterEntryLabel,
  type HireTeamRosterEntry,
} from "~/lib/marketplace/hire-team-roster";

type HireAddDepartmentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamRoster: HireTeamRosterEntry[];
  defaultLeadEmail?: string;
  initialValues?: HireDepartmentDetail;
  editIndex?: number | null;
  onAdd: (detail: HireDepartmentDetail) => void;
  onEdit?: (index: number, detail: HireDepartmentDetail) => void;
};

export function HireAddDepartmentDialog({
  open,
  onOpenChange,
  teamRoster,
  defaultLeadEmail,
  initialValues,
  editIndex = null,
  onAdd,
  onEdit,
}: HireAddDepartmentDialogProps) {
  const isEditing = editIndex != null && onEdit != null;

  const [values, setValues] = React.useState<HireDepartmentDetail>({
    name: "",
    leadTeamMemberEmail: defaultLeadEmail,
    otherTeamMemberEmails: [],
  });

  React.useEffect(() => {
    if (!open) return;
    setValues({
      name: initialValues?.name ?? "",
      leadTeamMemberEmail:
        initialValues?.leadTeamMemberEmail?.trim() || defaultLeadEmail || undefined,
      otherTeamMemberEmails: initialValues?.otherTeamMemberEmails ?? [],
    });
  }, [open, initialValues, defaultLeadEmail]);

  const leadOptions = teamRoster;
  const otherOptions = teamRoster.filter(
    (entry) => entry.email !== values.leadTeamMemberEmail,
  );

  const toggleOtherMember = (email: string, checked: boolean) => {
    setValues((prev) => {
      const current = prev.otherTeamMemberEmails ?? [];
      const next = checked
        ? [...current, email]
        : current.filter((item) => item !== email);
      return { ...prev, otherTeamMemberEmails: next };
    });
  };

  const canSubmit = values.name.trim() && values.leadTeamMemberEmail?.trim();

  const handleSubmit = () => {
    const detail: HireDepartmentDetail = {
      name: values.name.trim(),
      leadTeamMemberEmail: values.leadTeamMemberEmail?.trim(),
      otherTeamMemberEmails: (values.otherTeamMemberEmails ?? []).filter(Boolean),
    };

    if (isEditing && onEdit && editIndex != null) {
      onEdit(editIndex, detail);
    } else {
      onAdd(detail);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Department" : "Add Department"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="hire-dept-name">Department Name *</Label>
            <Input
              id="hire-dept-name"
              placeholder="Enter department name"
              value={values.name}
              onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>

          {teamRoster.length > 0 ? (
            <>
              <div className="space-y-2">
                <Label>Lead Team Member *</Label>
                <Select
                  value={values.leadTeamMemberEmail ?? ""}
                  onValueChange={(email) =>
                    setValues((prev) => ({
                      ...prev,
                      leadTeamMemberEmail: email,
                      otherTeamMemberEmails: (prev.otherTeamMemberEmails ?? []).filter(
                        (item) => item !== email,
                      ),
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select lead team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadOptions.map((entry) => (
                      <SelectItem key={entry.email} value={entry.email}>
                        {rosterEntryLabel(entry)}
                        {entry.role === "employer"
                          ? " · Employer"
                          : entry.role === "lead"
                            ? " · Lead"
                            : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {otherOptions.length > 0 ? (
                <div className="space-y-2">
                  <Label>Other Team Members</Label>
                  <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
                    {otherOptions.map((entry) => {
                      const checked = (values.otherTeamMemberEmails ?? []).includes(entry.email);
                      return (
                        <label
                          key={entry.email}
                          className="flex cursor-pointer items-start gap-2 text-sm text-slate-700"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(next) =>
                              toggleOtherMember(entry.email, next === true)
                            }
                          />
                          <span>{rosterEntryLabel(entry)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={handleSubmit}>
            {isEditing ? "Save Department" : "Add Department"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type HireDepartmentDetailCardProps = {
  detail: HireDepartmentDetail;
  teamRoster?: HireTeamRosterEntry[];
  onEdit?: () => void;
};

export function HireDepartmentDetailCard({
  detail,
  teamRoster = [],
  onEdit,
}: HireDepartmentDetailCardProps) {
  const leadMember = teamRoster.find(
    (entry) => entry.email === detail.leadTeamMemberEmail,
  );
  const otherMembers = (detail.otherTeamMemberEmails ?? [])
    .map((email) => teamRoster.find((entry) => entry.email === email))
    .filter((entry): entry is HireTeamRosterEntry => Boolean(entry));

  return (
    <div className="rounded-md border border-slate-100 bg-white px-3 py-2 text-sm text-slate-700">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-slate-900">{detail.name}</p>
        {onEdit ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 px-2 text-primary"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            Edit
          </Button>
        ) : null}
      </div>
      {leadMember ? (
        <p className="mt-2 text-xs text-slate-600">
          <span className="font-medium text-slate-700">Lead Team Member:</span> {leadMember.name}
        </p>
      ) : null}
      {otherMembers.length > 0 ? (
        <p className="mt-0.5 text-xs text-slate-600">
          <span className="font-medium text-slate-700">Other Team Members:</span>{" "}
          {otherMembers.map((member) => member.name).join(", ")}
        </p>
      ) : null}
    </div>
  );
}
